/**
 * ClaimCardBlock - Display a claim with evidence
 */

import React, { useState } from 'react'
import { ClaimCardBlock as ClaimCardBlockType } from '../../../../lib/cockpit/schemas'

interface ClaimCardBlockProps {
  data: ClaimCardBlockType['data']
  blockId: string
  title?: string
  compact?: boolean
  showExplain?: boolean
}

const CONFIDENCE_COLORS = {
  very_low: 'border-red-300 dark:border-red-700',
  low: 'border-orange-300 dark:border-orange-700',
  medium: 'border-yellow-300 dark:border-yellow-700',
  high: 'border-blue-300 dark:border-blue-700',
  very_high: 'border-green-300 dark:border-green-700'
}

function ClaimCardBlock({ data, blockId: _blockId, title: _title, compact, showExplain: _showExplain }: ClaimCardBlockProps) {
  const [expanded, setExpanded] = useState(false)
  const borderColor = CONFIDENCE_COLORS[data.confidence as keyof typeof CONFIDENCE_COLORS]

  if (compact) {
    return (
      <div className={`p-3 bg-white dark:bg-gray-800 rounded-lg border-l-4 ${borderColor}`}>
        <p className="text-gray-800 dark:text-gray-200">{data.claim}</p>
      </div>
    )
  }

  return (
    <div className={`bg-white dark:bg-gray-800 rounded-xl border-l-4 ${borderColor} shadow-sm overflow-hidden`}>
      {/* Claim */}
      <div className="p-4">
        <p className="text-gray-900 dark:text-white font-medium leading-relaxed">
          {data.claim}
        </p>
        
        {data.why_it_matters && (
          <div className="mt-3 flex gap-2">
            <svg className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
            </svg>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {data.why_it_matters}
            </p>
          </div>
        )}
      </div>

      {/* Counterpoint (collapsible) */}
      {data.counterpoint && (
        <div className="px-4 pb-4">
          <button
            onClick={() => setExpanded(!expanded)}
            className="flex items-center gap-2 text-sm text-orange-600 dark:text-orange-400 hover:underline"
          >
            <svg
              className={`w-4 h-4 transition-transform ${expanded ? 'rotate-90' : ''}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
            Counterpoint
          </button>
          {expanded && (
            <p className="mt-2 pl-6 text-sm text-gray-600 dark:text-gray-400 italic">
              {data.counterpoint}
            </p>
          )}
        </div>
      )}

      {/* Footer */}
      <div className="px-4 py-2 bg-gray-50 dark:bg-gray-800/50 border-t border-gray-100 dark:border-gray-700 flex items-center justify-between text-xs">
        <span className="text-gray-500 dark:text-gray-400">
          {typeof data.confidence === 'string' ? data.confidence.replace('_', ' ') : String(data.confidence || 'medium')} confidence
        </span>
        <span className="text-gray-400 dark:text-gray-500">
          {data.evidence_anchors.length} source{data.evidence_anchors.length !== 1 ? 's' : ''}
        </span>
      </div>
    </div>
  )
}

export default ClaimCardBlock



